import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';

import { validateCustomerInfo } from 'src/state-machines/purchase/functions/validate-customer-info';
import { RSFormMessageContainer } from 'src/reservation-page/forms/RSFormMessageContainer';

import { SectionFieldset } from 'src/organisms/product-purchase/SectionFieldset';
import { CustomerInfoInputTextField } from 'src/organisms/product-purchase/CustomerInfoInputTextField';
import { CustomerInfoInputTextArea } from 'src/organisms/product-purchase/CustomerInfoInputTextArea';
import { CustomerInfoInputSelect } from 'src/organisms/product-purchase/CustomerInfoInputSelect';
import { CustomerInfoInputRadio } from 'src/organisms/product-purchase/CustomerInfoInputRadio';
import { CustomerInfoInputCheckBox } from 'src/organisms/product-purchase/CustomerInfoInputCheckBox';
import { SubscriptionPurchaseFormValues } from 'src/pages/[publicId]/subscriptions/[productPublicId]/purchase';
import { ReservationFlowProductDetail } from 'src/interfaces/reservation_flow';
import { captureException } from '@sentry/nextjs';

type CustomerInfoFieldsetProps = {
  customerFields: ReservationFlowProductDetail['customer_fields'];
};
export const CustomerInfoFieldset: FC<CustomerInfoFieldsetProps> = ({
  customerFields,
}) => {
  const intl = useIntl();

  const { control } = useFormContext<SubscriptionPurchaseFormValues>();

  return (
    <SectionFieldset
      legend={intl.formatMessage({
        defaultMessage: '購入者情報',
        id: 'gYvZf+',
        description:
          'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
      })}
    >
      <div className="space-y-3">
        {customerFields.map((field) => {
          return (
            <div
              className="box-border border-b border-l-0 border-r-0 border-t-0 border-solid border-gray-50 pb-3 last-of-type:border-b-0 last-of-type:pb-0"
              key={`subscriptionPurchaseModal_customerInfo_${field.id}`}
            >
              {(() => {
                switch (field.answer_type) {
                  case 'text_field':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                          fieldState: { error },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          return (
                            <RSFormMessageContainer
                              errors={
                                typeof error !== 'undefined' &&
                                typeof error.message !== 'undefined'
                                  ? [error.message]
                                  : undefined
                              }
                              isBlock
                            >
                              <CustomerInfoInputTextField
                                ref={ref}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                item={field}
                                isError={typeof error !== 'undefined'}
                              />
                            </RSFormMessageContainer>
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            // FIXME: yupのバリデーションに寄せたい
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'wL1j1N',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'Ykwkrv',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'text_area':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                          fieldState: { error },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          return (
                            <RSFormMessageContainer
                              errors={
                                typeof error !== 'undefined' &&
                                typeof error.message !== 'undefined'
                                  ? [error.message]
                                  : undefined
                              }
                              isBlock
                            >
                              <CustomerInfoInputTextArea
                                ref={ref}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                item={field}
                                isError={typeof error !== 'undefined'}
                              />
                            </RSFormMessageContainer>
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'wL1j1N',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'Ykwkrv',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'select_box':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                          fieldState: { error },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          return (
                            <RSFormMessageContainer
                              errors={
                                typeof error !== 'undefined' &&
                                typeof error.message !== 'undefined'
                                  ? [error.message]
                                  : undefined
                              }
                              isBlock
                            >
                              <CustomerInfoInputSelect
                                ref={ref}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                item={field}
                                isError={typeof error !== 'undefined'}
                              />
                            </RSFormMessageContainer>
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'wL1j1N',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'Ykwkrv',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'radio_button':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          return (
                            <CustomerInfoInputRadio
                              ref={ref}
                              value={value}
                              onChange={onChange}
                              onBlur={onBlur}
                              item={field}
                            />
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'wL1j1N',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'Ykwkrv',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'checkbox':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                        }) => {
                          if (
                            typeof value !== 'undefined' &&
                            typeof value === 'string'
                          ) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          return (
                            <CustomerInfoInputCheckBox
                              ref={ref}
                              value={value}
                              onChange={onChange}
                              onBlur={onBlur}
                              item={field}
                            />
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'wL1j1N',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'Ykwkrv',
                                    description:
                                      'src/templates/merchants/purchase/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  default:
                    return null;
                }
              })()}
            </div>
          );
        })}
      </div>
    </SectionFieldset>
  );
};
